import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { RefreshCw, ArrowDown, ArrowUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { getBankTransactions, getLiveBankBalance, type PulledTransaction } from "@/lib/api";

interface Props {
  connectionId: string;
  storedBalance: string | null;
  storedBalanceAsOf: string | null;
  onSync: () => void;
  syncing: boolean;
}

export function BankTransactionFeed({ connectionId, storedBalance, storedBalanceAsOf, onSync, syncing }: Props) {
  const [page, setPage] = useState(0);
  const [allTxns, setAllTxns] = useState<PulledTransaction[]>([]);
  const [liveBalance, setLiveBalance] = useState<{ available: string; ledger: string } | null>(null);
  const [fetchingBalance, setFetchingBalance] = useState(false);

  async function handleRefreshBalance() {
    setFetchingBalance(true);
    try {
      const bal = await getLiveBankBalance(connectionId);
      setLiveBalance(bal);
    } finally {
      setFetchingBalance(false);
    }
  }

  const fmt = (n: string) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(parseFloat(n));

  const { data: txns = [], isLoading, isFetching } = useQuery({
    queryKey: ["bank-transactions", connectionId, page],
    queryFn: async () => {
      const results = await getBankTransactions(connectionId, page);
      if (page === 0) {
        setAllTxns(results);
      } else {
        setAllTxns((prev) => [...prev, ...results]);
      }
      return results;
    },
    staleTime: 60_000,
  });

  const hasMore = txns.length === 50;

  function formatAmount(amount: string, type: string | null): string {
    const num = parseFloat(amount);
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(num);
  }

  function isDebit(type: string | null): boolean {
    return type === "debit";
  }

  if (isLoading && page === 0) {
    return (
      <div className="py-4 text-center text-sm text-muted-foreground">
        <RefreshCw className="w-4 h-4 animate-spin inline mr-1" />
        Loading transactions…
      </div>
    );
  }

  if (allTxns.length === 0) {
    return (
      <div className="py-4 text-center text-sm text-muted-foreground">
        No transactions yet.{" "}
        <button
          className="text-blue-600 hover:underline"
          onClick={onSync}
          disabled={syncing}
        >
          Sync now
        </button>{" "}
        to load.
      </div>
    );
  }

  const displayBalance = liveBalance ?? (storedBalance ? { available: storedBalance, ledger: storedBalance } : null);

  return (
    <div className="mt-2 space-y-2">
      {/* Balance row */}
      <div className="flex items-center justify-between px-1">
        <div className="flex items-center gap-4 text-sm">
          {displayBalance ? (
            <>
              <span className="text-muted-foreground">
                Ledger: <span className="font-semibold text-foreground">{fmt(displayBalance.ledger)}</span>
              </span>
              {liveBalance && liveBalance.available !== liveBalance.ledger && (
                <span className="text-muted-foreground">
                  Available: <span className="font-semibold text-foreground">{fmt(liveBalance.available)}</span>
                </span>
              )}
              {storedBalanceAsOf && !liveBalance && (
                <span className="text-xs text-muted-foreground">
                  as of {new Date(storedBalanceAsOf).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                </span>
              )}
              {liveBalance && <span className="text-xs text-green-600">Live</span>}
            </>
          ) : (
            <span className="text-xs text-muted-foreground">Balance not yet synced</span>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-xs text-muted-foreground"
          onClick={handleRefreshBalance}
          disabled={fetchingBalance}
        >
          <RefreshCw className={`w-3 h-3 mr-1 ${fetchingBalance ? "animate-spin" : ""}`} />
          {fetchingBalance ? "Fetching…" : "Live balance"}
        </Button>
      </div>

      <div className="border rounded-md overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50 border-b text-muted-foreground">
              <th className="px-3 py-2 text-left font-medium">Date</th>
              <th className="px-3 py-2 text-left font-medium">Description</th>
              <th className="px-3 py-2 text-right font-medium">Amount</th>
              <th className="px-3 py-2 text-left font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {allTxns.map((txn) => (
              <tr key={txn.id} className="border-b last:border-0 hover:bg-gray-50">
                <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                  {txn.date}
                </td>
                <td className="px-3 py-2">
                  {txn.description}
                </td>
                <td className={`px-3 py-2 text-right font-medium whitespace-nowrap flex items-center justify-end gap-0.5 ${isDebit(txn.type) ? "text-red-600" : "text-green-600"}`}>
                  {isDebit(txn.type)
                    ? <ArrowDown className="w-3 h-3 inline" />
                    : <ArrowUp className="w-3 h-3 inline" />}
                  {formatAmount(txn.amount, txn.type)}
                </td>
                <td className="px-3 py-2">
                  {txn.status === "pending" ? (
                    <Badge variant="outline" className="text-xs border-amber-300 text-amber-600 bg-amber-50">
                      Pending
                    </Badge>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {hasMore && (
        <div className="mt-2 text-center">
          <Button
            variant="ghost"
            size="sm"
            className="text-xs text-muted-foreground"
            onClick={() => setPage((p) => p + 1)}
            disabled={isFetching}
          >
            {isFetching ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : null}
            Load more
          </Button>
        </div>
      )}
    </div>
  );
}
